import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, createBlock as _createBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createVNode as _createVNode, withCtx as _withCtx, createSlots as _createSlots } from "vue"

const _hoisted_1 = { class: "p-4 w-full border-b dark:border-gray-900" }
const _hoisted_2 = { class: "p-4 -mt-2" }
const _hoisted_3 = { key: 0 }
const _hoisted_4 = { class: "flex justify-between items-start" }
const _hoisted_5 = { class: "flex" }
const _hoisted_6 = { class: "lowercase" }
const _hoisted_7 = { class: "flex justify-between pt-4 font-medium" }
const _hoisted_8 = { class: "text-right" }

import { computed, toRef } from 'vue';
import { useRoute } from 'vue-router';

// Composables
import useNumbers, { FNumFormats } from '@/composables/useNumbers';
import { usePool } from '@/composables/usePool';
import useTokens from '@/composables/useTokens';
import { bnum, isSameAddress } from '@/lib/utils';
import { Pool } from '@/services/pool/types';

// Components
import AssetRow from './components/AssetRow.vue';

/**
 * TYPES
 */
type Props = {
  pool: Pool;
  useNativeAsset: boolean;
  hideHeader?: boolean;
};

/**
 * PROPS & EMITS
 */

export default /*#__PURE__*/_defineComponent({
  props: {
    pool: null,
    useNativeAsset: { type: Boolean },
    hideHeader: { type: Boolean, default: false }
  },
  emits: ["update:useNativeAsset"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'update:useNativeAsset', value: boolean): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { pool: Pool, useNativeAsset: boolean, hideHeader: boolean }





/**
 * COMPOSABLES
 */
const { isWethPool, isStablePhantomPool } = usePool(toRef(props, 'pool'));
const { balanceFor, nativeAsset, wrappedNativeAsset } = useTokens();
const { fNum2, toFiat } = useNumbers();
const route = useRoute();

/**
 * COMPUTED
 */
const pageContext = computed(() => route.name);

const tokenAddresses = computed((): string[] => {
  if (isStablePhantomPool.value) {
    return props.pool.mainTokens || [];
  }

  return props.pool.tokensList;
});

const tokensForTotal = computed((): string[] => {
  if (pageContext.value === 'invest' && props.useNativeAsset) {
    return tokenAddresses.value.map(address => {
      if (isSameAddress(address, wrappedNativeAsset.value.address))
        return nativeAsset.address;
      return address;
    });
  } else if (pageContext.value === 'withdraw' && isWethPool.value) {
    return [nativeAsset.address, ...tokenAddresses.value];
  }

  return tokenAddresses.value;
});

const fiatTotal = computed(() => {
  const fiatValue = tokensForTotal.value
    .map(address => {
      if (pageContext.value === 'invest') {
        if (
          isSameAddress(address, nativeAsset.address) &&
          !props.useNativeAsset
        )
          return '0';
        if (
          isSameAddress(address, wrappedNativeAsset.value.address) &&
          props.useNativeAsset
        )
          return '0';
      }

      const tokenBalance = balanceFor(address);
      return toFiat(tokenBalance, address);
    })
    .reduce((total, value) => bnum(total).plus(value).toString());

  return fNum2(fiatValue, FNumFormats.fiat);
});

/**
 * METHODS
 */
function isSelectedNativeAsset(address: string): boolean {
  if (pageContext.value === 'withdraw') return true;
  if (props.useNativeAsset && isSameAddress(address, nativeAsset.address))
    return true;

  return (
    !props.useNativeAsset &&
    isSameAddress(address, wrappedNativeAsset.value.address)
  );
}

return (_ctx: any,_cache: any) => {
  const _component_BalTooltip = _resolveComponent("BalTooltip")!
  const _component_BalBreakdown = _resolveComponent("BalBreakdown")!
  const _component_BalCard = _resolveComponent("BalCard")!

  return (_openBlock(), _createBlock(_component_BalCard, {
    shadow: "none",
    noPad: ""
  }, _createSlots({
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_2, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(tokenAddresses), (address) => {
          return (_openBlock(), _createElementBlock("div", {
            key: address,
            class: "py-2"
          }, [
            (_unref(isSameAddress)(address, _unref(wrappedNativeAsset).address))
              ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                  _createElementVNode("div", _hoisted_4, [
                    _createVNode(_component_BalBreakdown, {
                      items: [_unref(nativeAsset), _unref(wrappedNativeAsset)],
                      class: "w-full",
                      size: "lg"
                    }, {
                      item: _withCtx(({ item: asset }) => [
                        _createVNode(AssetRow, {
                          address: asset.address,
                          selected: isSelectedNativeAsset(asset.address),
                          class: _normalizeClass([{ 'cursor-pointer': _unref(pageContext) === 'invest' }]),
                          onClick: ($event: any) => (
                    emit(
                      'update:useNativeAsset',
                      _unref(isSameAddress)(asset.address, _unref(nativeAsset).address)
                    )
                  )
                        }, null, 8, ["address", "selected", "class", "onClick"])
                      ]),
                      default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_5, [
                          _createElementVNode("span", null, [
                            _createTextVNode(_toDisplayString(_unref(nativeAsset).name) + " ", 1),
                            _createElementVNode("span", _hoisted_6, _toDisplayString(_ctx.$t('tokens')), 1)
                          ]),
                          (_unref(pageContext) === 'invest')
                            ? (_openBlock(), _createBlock(_component_BalTooltip, {
                                key: 0,
                                class: "mt-1 ml-2",
                                iconSize: "sm",
                                text: 
                    _ctx.$t(
                      'poolTransfer.myWalletTokensCard.tooltips.nativeAssetSelection',
                      [_unref(nativeAsset).symbol, _unref(wrappedNativeAsset).symbol]
                    )
                  
                              }, null, 8, ["text"]))
                            : _createCommentVNode("", true)
                        ])
                      ]),
                      _: 2
                    }, 1032, ["items"])
                  ])
                ]))
              : (_openBlock(), _createBlock(AssetRow, {
                  key: 1,
                  address: address,
                  selected: true
                }, null, 8, ["address"]))
          ]))
        }), 128)),
        _createElementVNode("div", _hoisted_7, [
          _createElementVNode("span", null, _toDisplayString(_ctx.$t('total')), 1),
          _createElementVNode("span", _hoisted_8, _toDisplayString(_unref(fiatTotal)), 1)
        ])
      ])
    ]),
    _: 2
  }, [
    (!__props.hideHeader)
      ? {
          name: "header",
          fn: _withCtx(() => [
            _createElementVNode("div", _hoisted_1, [
              _createElementVNode("h6", null, _toDisplayString(_ctx.$t('poolTransfer.myWalletTokensCard.title')), 1)
            ])
          ])
        }
      : undefined
  ]), 1024))
}
}

})